import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import AddTeamMemberModal from '../../components/ui/AddTeamMemberModal';
import { listStaff, deleteStaff, updateStaff, createStaff, resendVerificationEmail } from '../../services/staff';
import { listRoles } from '../../services/roles';

const AdminTeam = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [roles, setRoles] = useState([]);

  const [teamMembers, setTeamMembers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Load staff members from backend
  const loadStaffMembers = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);
      const response = await listStaff({ page, limit: 20, search });
      // Normalize IDs: convert _id to id for consistency
      // Also ensure roleIds is properly formatted
      const normalizedData = (response.data || []).map(member => ({
        ...member,
        id: member._id || member.id,
        // Ensure roleIds is an array (handle both populated and non-populated)
        roleIds: member.roleIds || (member.roles ? member.roles.map(r => r._id || r.id || r) : [])
      }));
      setTeamMembers(normalizedData);
      setPagination({
        page: response.page || response.pagination?.page || 1,
        limit: response.limit || response.pagination?.limit || 20,
        total: response.total || response.pagination?.total || 0,
        totalPages: response.totalPages || Math.ceil((response.total || response.pagination?.total || 0) / 20)
      });
    } catch (err) {
      console.error('Failed to load staff members:', err);
      setError('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  };

  // Load roles for dropdown
  const loadRoles = async () => {
    try {
      const response = await listRoles();
      setRoles(response.data || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  useEffect(() => {
    loadStaffMembers();
    loadRoles();
  }, []);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    loadStaffMembers(1, query);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    loadStaffMembers(newPage, searchQuery);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!memberToDelete) return;
    
    try {
      const memberId = memberToDelete.id || memberToDelete._id;
      if (!memberId) {
        setError('Invalid member ID');
        return;
      }
      
      await deleteStaff(memberId);
      // Reload the list to ensure consistency
      await loadStaffMembers(pagination.page, searchQuery);
      setIsDeleteModalOpen(false);
      setMemberToDelete(null);
    } catch (err) {
      console.error('Failed to delete staff member:', err);
      setError(err.response?.data?.error || 'Failed to delete staff member');
    }
  };

  // Mock data for development (remove when backend is fully integrated)
  const mockTeamMembers = [
    {
      id: 1,
      firstName: 'Alice',
      lastName: 'Smith',
      email: 'alice@example.com',
      phone: '(555) 123-4567',
      permissions: 'Reviewer',
      status: 'Available'
    },
    {
      id: 2,
      firstName: 'Bob',
      lastName: 'Johnson',
      email: 'bob@example.com',
      phone: '(555) 234-5678',
      permissions: 'Approver',
      status: 'Unavailable'
    },
    {
      id: 3,
      firstName: 'Charlie',
      lastName: 'Williams',
      email: 'charlie@example.com',
      phone: '(555) 345-6789',
      permissions: 'Assistant',
      status: 'Unavailable'
    },
    {
      id: 4,
      firstName: 'Diana',
      lastName: 'Brown',
      email: 'diana@example.com',
      phone: '(555) 456-7890',
      permissions: 'Manager',
      status: 'Available'
    },
    {
      id: 5,
      firstName: 'Ethan',
      lastName: 'Jones',
      email: 'ethan@example.com',
      phone: '(555) 567-8901',
      permissions: 'Coordinator',
      status: 'Unavailable'
    },
    {
      id: 6,
      firstName: 'Fiona',
      lastName: 'Garcia',
      email: 'fiona@example.com',
      phone: '(555) 678-9012',
      permissions: 'Supervisor',
      status: 'Available'
    },
    {
      id: 7,
      firstName: 'George',
      lastName: 'Martinez',
      email: 'george@example.com',
      phone: '(555) 789-0123',
      permissions: 'Consultant',
      status: 'Unavailable'
    },
    {
      id: 8,
      firstName: 'Hannah',
      lastName: 'Hernandez',
      email: 'hannah@example.com',
      phone: '(555) 890-1234',
      permissions: 'Facilitator',
      status: 'Available'
    },
    {
      id: 9,
      firstName: 'Isaac',
      lastName: 'Lopez',
      email: 'isaac@example.com',
      phone: '(555) 901-2345',
      permissions: 'Administrator',
      status: 'Unavailable'
    },
    {
      id: 10,
      firstName: 'Jasmine',
      lastName: 'Gonzalez',
      email: 'jasmine@example.com',
      phone: '(555) 012-3456',
      permissions: 'Director',
      status: 'Unavailable'
    },
    {
      id: 11,
      firstName: 'Kevin',
      lastName: 'Wilson',
      email: 'kevin@example.com',
      phone: '(555) 123-4568',
      permissions: 'Lead',
      status: 'Unavailable'
    }
  ];

  const columns = [
    {
      key: 'fullName',
      label: 'Full Name',
      render: (value) => <span className="text-[#73827c]">{value}</span>
    },
    {
      key: 'email',
      label: 'Email',
      render: (value) => <span className="text-[#73827c]">{value}</span>
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (value) => <span className="text-[#73827c]">{value || 'N/A'}</span>
    },
    {
      key: 'roles',
      label: 'Roles',
      render: (value, row) => {
        const roleNames = row.roles?.map(role => role.name).join(', ') || 'No roles';
        return <span className="text-[#73827c]">{roleNames}</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
            value === 'active'
              ? 'bg-[#f0fff2] text-[#40c191]'
              : 'bg-[#fff8e6] text-[#f4a340]'
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              value === 'active' ? 'bg-[#40c191]' : 'bg-[#f4a340]'
            }`}
          />
          {value === 'active' ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  const handleAddMember = async (newMember) => {
    try {
      console.log('Creating staff member:', newMember);
      await createStaff(newMember);
      await loadStaffMembers(pagination.page, searchQuery);
      setIsAddModalOpen(false);
    } catch (err) {
      console.error('Failed to create staff member:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create staff member';
      setError(errorMsg);
      throw err; // Re-throw to let modal handle it
    }
  };

  const handleAction = async (action, member) => {
    if (action === 'delete') {
      setMemberToDelete(member);
      setIsDeleteModalOpen(true);
    } else if (action === 'edit') {
      // For now, we'll use the same modal for editing
      // You can create a separate EditTeamMemberModal component later
      console.log('Editing member:', member);
      setMemberToEdit(member);
      setIsEditModalOpen(true);
    } else if (action === 'view') {
      const memberId = member.id || member._id;
      navigate(`/settings/team-member/${memberId}`);
    } else if (action === 'resend-verification') {
      await handleResendVerificationEmail(member);
    }
  };

  const handleResendVerificationEmail = async (member) => {
    try {
      setError(null);
      setSuccess(null);
      const memberId = member.id || member._id;
      if (!memberId) {
        setError('Invalid member ID');
        return;
      }

      await resendVerificationEmail(memberId);
      setSuccess(`Verification email sent successfully to ${member.email}`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Failed to resend verification email:', err);
      setError(err.response?.data?.error || err.message || 'Failed to resend verification email');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  const handleDeleteConfirm = () => {
    handleDelete();
  };

  const handleEditMember = async (updatedMember) => {
    try {
      const memberId = memberToEdit?.id || memberToEdit?._id;
      if (!memberId) {
        const errorMsg = 'Invalid member ID';
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      
      console.log('Updating staff member:', { memberId, updatedMember });
      const response = await updateStaff(memberId, updatedMember);
      console.log('Update response:', response);
      
      await loadStaffMembers(pagination.page, searchQuery);
      setIsEditModalOpen(false);
      setMemberToEdit(null);
    } catch (err) {
      console.error('Failed to update staff member:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update staff member';
      setError(errorMsg);
      throw err; // Re-throw to let modal handle it
    }
  };

  const actionMenuItems = [
    {
      label: 'View Details',
      action: 'view',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10.3866 8.00001C10.3866 9.31334 9.31995 10.38 7.99995 10.38C6.67995 10.38 5.61328 9.31334 5.61328 8.00001C5.61328 6.68668 6.67995 5.62001 7.99995 5.62001C9.31995 5.62001 10.3866 6.68668 10.3866 8.00001Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.00004 13.5133C10.3534 13.5133 12.5467 12.1267 14.0734 9.72668C14.6734 8.78668 14.6734 7.20668 14.0734 6.26668C12.5467 3.86668 10.3534 2.48001 8.00004 2.48001C5.64671 2.48001 3.45337 3.86668 1.92671 6.26668C1.32671 7.20668 1.32671 8.78668 1.92671 9.72668C3.45337 12.1267 5.64671 13.5133 8.00004 13.5133Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    },
    {
      label: 'Edit Member',
      action: 'edit',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M7.33333 1.33334H6C2.66667 1.33334 1.33333 2.66668 1.33333 6.00001V10C1.33333 13.3333 2.66667 14.6667 6 14.6667H10C13.3333 14.6667 14.6667 13.3333 14.6667 10V8.66668"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.6933 2.01333L5.44 7.26666C5.24 7.46666 5.04 7.86 5 8.14666L4.71333 10.1533C4.60667 10.8733 5.12667 11.3867 5.84667 11.2867L7.85333 11C8.13333 10.96 8.52667 10.76 8.73333 10.56L13.9867 5.30666C14.8933 4.39999 15.32 3.34666 13.9867 2.01333C12.6533 0.679997 11.6 1.10666 10.6933 2.01333Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.94 2.76666C10.3867 4.36 11.64 5.61333 13.24 6.06666"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    },
    {
      label: 'Resend Verification Email',
      action: 'resend-verification',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 2.66667V5.33333L11.3333 2.66667L8 0V2.66667Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2.66667 8C2.66667 10.9455 5.05448 13.3333 8 13.3333C9.41449 13.3333 10.7711 12.7714 11.7712 11.7712C12.7714 10.7711 13.3333 9.41449 13.3333 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8 13.3333V10.6667L4.66667 13.3333L8 16V13.3333Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.3333 8C13.3333 5.05448 10.9455 2.66667 8 2.66667C6.58551 2.66667 5.22886 3.22857 4.22876 4.22876C3.22867 5.22886 2.66667 6.58551 2.66667 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    },
    {
      label: 'Delete',
      action: 'delete',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M14 3.98666C11.78 3.76666 9.54667 3.65333 7.32 3.65333C6 3.65333 4.68 3.72 3.36 3.85333L2 3.98666"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.66667 3.31333L5.81333 2.44C5.92 1.80667 6 1.33333 7.12667 1.33333H8.87333C10 1.33333 10.0867 1.83333 10.1867 2.44667L10.3333 3.31333"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.5667 6.09333L12.1333 12.8067C12.06 13.8533 12 14.6667 10.1400 14.6667H5.86001C4.00001 14.6667 3.94001 13.8533 3.86668 12.8067L3.43335 6.09333"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.88667 11H9.10667"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.33333 8.33333H9.66667"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      danger: true
    }
  ];

  // Search is now handled by the backend, so we use teamMembers directly
  const filteredMembers = teamMembers;

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Admin Team</h1>
        <div className="flex items-center gap-3">
          {/* Filter Button */}
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M4.16667 5.83333H15.8333"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M6.66667 10H13.3333"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M9.16667 14.1667H10.8333"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Filter
          </button>

          {/* Search Input */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.5 17.5L13.875 13.875"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search staff members..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent w-64"
            />
          </div>

          {/* Add Team Member Button */}
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white px-6 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Add Team Member
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.5 10L9.16667 11.6667L12.5 8.33333"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{success}</span>
          </div>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-600 hover:text-green-800"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 6.66667V10M10 13.3333H10.0083"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{error}</span>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredMembers}
        actionMenuItems={actionMenuItems}
        onAction={handleAction}
      />

      {/* Add Team Member Modal */}
      <AddTeamMemberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddMember}
        roles={roles}
      />

      {/* Edit Team Member Modal */}
      {memberToEdit && (
        <AddTeamMemberModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setMemberToEdit(null);
          }}
          onAdd={handleEditMember}
          roles={roles}
          initialData={memberToEdit}
          isEdit={true}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setMemberToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Team Member"
        message="Are you sure you want to remove this team member? This action cannot be undone."
        itemName={memberToDelete ? (memberToDelete.fullName || `${memberToDelete.firstName || ''} ${memberToDelete.lastName || ''}`.trim() || 'this team member') : ''}
      />
    </div>
  );
};

export default AdminTeam;

